import { useState, useEffect, useRef } from 'react';

type ClientTimerState = {
  /** Current client-side time in seconds (matches server timeSpent concept) */
  clientTimeSpentSeconds: number;
  /** Timestamp when current cycle started */
  cycleStartTime: number;
  /** Server time at the start of current cycle (informative only) */
  cycleStartServerTime: number;
  /** Whether any activity was detected in current cycle (informative only) */
  hasActivityInCycle: boolean;
  /** Whether timer is actively running (progressing second by second) */
  isRunning: boolean;
  /** Timestamp of last successful server sync */
  lastServerSync: number;
  /** Number of consecutive failed /tick cycles */
  failedCycles: number;
  /** Timestamp when last failure was recorded (to prevent duplicate counting) */
  lastFailureTime: number;
  /** Timestamp when pause started (null if not paused) */
  pauseStartTime: number | null;
  /** Total time spent paused in current cycle (in seconds) */
  totalPausedSecondsInCycle: number;
  /** Timestamp of last tick update, to progress the timer the correct amount in the next tick */
  lastTickUpdateTime: number;
};

export type ClientTimer = Pick<
  ClientTimerState,
  'clientTimeSpentSeconds' | 'isRunning' | 'hasActivityInCycle' | 'failedCycles'
> & {
  /** Whether the timer data might be stale eg. due to network issues */
  isStale: boolean;
};

/**
 * Client timer that continuously progresses to match server behavior:
 * - Timer runs (progresses second-by-second) unless paused or network issues
 * - Syncs with server on each /tick response
 * - Tracks user activity for informative purposes (but doesn't affect timer progression)
 * - Handles network failures gracefully with failure tolerance
 *
 * @param serverTimeSpentSeconds - Time spent from server /tick response
 * @param tickIntervalSeconds - Server tick interval (20-30s typically)
 * @param bufferMs - Extra time to account for network delays (default 2s)
 * @param isPaused - Whether timer is paused (stops client timer)
 * @param isSubmittingTask - Whether task is being submitted (stops client timer)
 */
export function useClientTimer({
  serverTimeSpentSeconds,
  tickIntervalSeconds = 30,
  bufferMs = 2000,
  isPaused = false,
  isSubmittingTask,
}: {
  serverTimeSpentSeconds: number;
  tickIntervalSeconds?: number;
  bufferMs?: number;
  isPaused?: boolean;
  isSubmittingTask?: boolean;
}): ClientTimer {
  const now = Date.now();
  const [state, setState] = useState<ClientTimerState>({
    clientTimeSpentSeconds: serverTimeSpentSeconds,
    cycleStartTime: now,
    cycleStartServerTime: serverTimeSpentSeconds,
    hasActivityInCycle: false,
    isRunning: !isPaused, // start running (unless paused)
    lastServerSync: now,
    failedCycles: 0,
    lastFailureTime: 0,
    pauseStartTime: isPaused ? now : null,
    totalPausedSecondsInCycle: 0,
    lastTickUpdateTime: now,
  });

  const lastActivity = useRef(new Date());

  // Activity detection using same events as withV2Timer (for informative purposes, doesn't affect actual timer progression)
  useEffect(() => {
    const handleActivity = () => {
      lastActivity.current = new Date();

      // Don't process activity if timer is paused
      if (isPaused) {
        return;
      }

      // Update internal active state - for informative purposes only
      // this would help reflecting an "active time spent" timer
      // but the API currently only returns the total time spent, regardless
      // of the project config.useTotalTime option for final Pay logic
      setState(prev => ({
        ...prev,
        hasActivityInCycle: true,
      }));
    };

    // Use same events as withV2Timer.tsx
    document.addEventListener('mousemove', handleActivity);
    document.addEventListener('keydown', handleActivity);

    return () => {
      document.removeEventListener('mousemove', handleActivity);
      document.removeEventListener('keydown', handleActivity);
    };
  }, [isPaused]);

  // Server sync handling - start new cycle on server update
  const prevServerTime = useRef(serverTimeSpentSeconds);
  useEffect(() => {
    const now_ = Date.now();

    // Check if server time actually changed (new tick response received)
    if (serverTimeSpentSeconds !== prevServerTime.current) {
      // Handle server time sync - reset to server time
      setState(prev => ({
        ...prev,
        clientTimeSpentSeconds: serverTimeSpentSeconds,
        cycleStartTime: now_,
        cycleStartServerTime: serverTimeSpentSeconds,
        hasActivityInCycle: false,
        lastServerSync: now_,
        // reset failure state
        failedCycles: 0,
        lastFailureTime: 0,
        totalPausedSecondsInCycle: 0,
        lastTickUpdateTime: now_,
        // if stopped running, resume if not in a stopped state anymore
        isRunning: !isPaused && !isSubmittingTask,
      }));

      prevServerTime.current = serverTimeSpentSeconds;
    }
  }, [serverTimeSpentSeconds, isPaused, isSubmittingTask]);

  // Pause/submission handling - track pause duration, stop/resume timer
  useEffect(() => {
    const now_ = Date.now();

    if (isPaused || isSubmittingTask) {
      // stop timer when paused or submitting
      // if paused, also record pause start time
      setState(prev => ({
        ...prev,
        isRunning: false,
        hasActivityInCycle: false,
        pauseStartTime:
          isPaused && !prev.pauseStartTime
            ? now_ // (only set new time if not already paused)
            : prev.pauseStartTime,
      }));
    } else {
      // Ending pause, or submit failed
      // -> resume timer
      // (and calculate paused duration if resuming from pause)
      setState(prev => {
        const pauseDurationSeconds =
          prev.pauseStartTime !== null
            ? // is resuming from pause
              (now_ - prev.pauseStartTime) / 1000
            : 0;

        return {
          ...prev,
          isRunning: true, // Resume timer
          pauseStartTime: null,
          totalPausedSecondsInCycle: prev.totalPausedSecondsInCycle + pauseDurationSeconds,
          lastTickUpdateTime: now_, // Reset to avoid large jumps
          // Reset failure state on resume to allow timer to restart
          failedCycles: 0,
          lastFailureTime: 0,
        };
      });
    }
  }, [isPaused, isSubmittingTask]);

  // Client timer progression and network failure handling
  useEffect(() => {
    const interval = setInterval(() => {
      setState(prev => {
        if (isPaused) {
          // don't process anything when paused
          return prev;
        }

        const now_ = Date.now();
        const timeSinceCycleStart = now_ - prev.cycleStartTime;
        const timeSinceLastSync = now_ - prev.lastServerSync;
        const timeSinceLastTickUpdate = now_ - prev.lastTickUpdateTime;

        const tickIntervalMs = tickIntervalSeconds * 1000;

        // 1. Check for cycle failures
        const totalPausedTimeMs = prev.totalPausedSecondsInCycle * 1000;
        const currentPauseTimeMs = isPaused && prev.pauseStartTime ? now_ - prev.pauseStartTime : 0;
        const totalPausedMs = totalPausedTimeMs + currentPauseTimeMs;

        // adjust timing calculations to exclude paused time
        const activeTimeSinceCycleStartMs = timeSinceCycleStart - totalPausedMs;
        const activeTimeSinceLastSyncTimeMs = timeSinceLastSync - totalPausedMs;

        const isCycleFailure =
          // cycle has been active (excluding pauses) for more than 1 tick interval
          activeTimeSinceCycleStartMs > tickIntervalMs + bufferMs &&
          // and last server sync was more than 1.5 tick intervals ago (excluding pauses)
          activeTimeSinceLastSyncTimeMs > tickIntervalMs * 1.5;

        if (isCycleFailure) {
          // Only count failure once per cycle (prevent rapid incrementing)
          const shouldCountFailure = now_ - prev.lastFailureTime > tickIntervalMs;

          if (!shouldCountFailure) {
            // Already counted this cycle's failure, don't increment again
            return prev;
          }

          // handle cycle failure (eg. /tick response not received successfully)
          if (prev.failedCycles > 0) {
            // second failure -> stop timer until server recovery
            return {
              ...prev,
              isRunning: false,
              failedCycles: prev.failedCycles + 1,
              lastFailureTime: now_,
              lastTickUpdateTime: now_,
            };
          }
          // first failure -> start new cycle
          return {
            ...prev,
            cycleStartTime: now_,
            hasActivityInCycle: false,
            isRunning: false,
            failedCycles: prev.failedCycles + 1,
            lastFailureTime: now_,
            lastTickUpdateTime: now_,
          };
        }

        // 2. Progress timer, if running
        if (!prev.isRunning) {
          // not running -> don't progress clientTimeSpentSeconds timer
          return prev;
        }

        // Calculate actual elapsed seconds since last update
        // this is more accurate than just progressing the interval time, as setInterval isn't fully reliable
        const elapsedSeconds = timeSinceLastTickUpdate / 1000;

        // is running -> progress clientTimeSpentSeconds timer
        return {
          ...prev,
          clientTimeSpentSeconds: prev.clientTimeSpentSeconds + elapsedSeconds,
          lastTickUpdateTime: now_,
        };
      });
    }, 500);

    return () => clearInterval(interval);
  }, [tickIntervalSeconds, bufferMs, isPaused]);

  const { clientTimeSpentSeconds, isRunning, hasActivityInCycle, failedCycles } = state;

  // floor to avoid too many decimal value changes causing unnecessary re-renders
  const clientTimeSpentSeconds_ = Math.floor(clientTimeSpentSeconds);

  return {
    clientTimeSpentSeconds: clientTimeSpentSeconds_,
    isStale: failedCycles > 1,
    isRunning,
    hasActivityInCycle,
    failedCycles,
  };
}
